import React from "react";
import { Edit, Trash2, Award, Receipt, FileDown } from "lucide-react";
import { formatTL, fmtDate } from "@/lib/api";
import { CIKIS_SEBEP_LABELS } from "./constants";
import { Th } from "./shared";
import { usePrimRateRules } from "@/hooks/usePrimRateRules";
import { DocsMenu } from "./DocsMenu";
import { StatementActions } from "./StatementActions";
import { SortTh, SortProvider, useColumnSort } from "@/components/SortTh";
import { LoadingRow } from "@/components/LoadingState";

const NUM_KEYS = ["aylik_brut"];
const TH_CLS = "px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500";

export default function PersonnelTable({
  rows,
  loading,
  view,
  isAdmin,
  canManageStatements,
  onStatementChanged,
  statementViews,
  canSeeKidem,
  canSeeMoney,
  hideCCRateBadge,
  selected,
  allSelected,
  onToggleSelect,
  onToggleSelectAll,
  onEdit,
  onDelete,
  onAdvance,
  onKidemSim,
  onExitPdf,
  docsIndex,
  onDocsChanged,
}) {
  const { getRuleFor, isCallCenterPozisyon } = usePrimRateRules();
  const { sorted, sort, toggle } = useColumnSort(rows, NUM_KEYS);
  return (
    <div className="hidden md:block sticky-table">
      <SortProvider value={{ sort, toggle }}>
      <table className="personnel-table w-full" data-sel={view === "aktif" && (isAdmin || canManageStatements) ? "1" : "0"} data-testid="personnel-table">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {view === "aktif" && (isAdmin || canManageStatements) && (
              <th className="px-3 py-3 w-10">
                <input
                  data-testid="select-all"
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                />
              </th>
            )}
            <Th k="sicil_no">TC Kimlik No</Th>
            <Th k="ad_soyad">Ad Soyad</Th>
            <SortTh k="pozisyon" label="Pozisyon" sort={sort} onSort={toggle} className={`${TH_CLS} text-left`} testid="personnel-sort-pozisyon" />
            {canSeeMoney
              ? <SortTh k="aylik_brut" label="Aylık Brüt" sort={sort} onSort={toggle} className={`${TH_CLS} text-right`} testid="personnel-sort-aylik_brut" />
              : <Th align="right">Aylık Brüt</Th>}
            <SortTh k="ise_baslama_tarihi" label="İşe Başlama" sort={sort} onSort={toggle} className={`${TH_CLS} text-left`} testid="personnel-sort-ise_baslama_tarihi" />
            {view === "pasif" && <Th k="ise_cikis_tarihi">Çıkış Tarihi</Th>}
            {view === "pasif" && <Th k="cikis_sebebi">Sebep</Th>}
            <Th k="aktif">Durum</Th>
            <Th align="right">İşlemler</Th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <LoadingRow colSpan={9} text="Personel listesi yükleniyor..." />
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={9} className="text-center py-10 text-slate-400">
              {view === "pasif" ? "Pasif personel yok" : "Henüz aktif personel kaydı yok"}
            </td></tr>
          )}
          {sorted.map((p) => (
            <tr
              key={p.id}
              data-testid={`personnel-row-${p.sicil_no}`}
              className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(p.id) ? "bg-amber-50" : ""}`}
            >
              {view === "aktif" && (isAdmin || canManageStatements) && (
                <td className="px-3 py-3">
                  <input
                    data-testid={`select-row-${p.sicil_no}`}
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => onToggleSelect(p.id)}
                  />
                </td>
              )}
              <td className="px-5 py-3 font-mono-tab text-sm text-slate-700">{p.sicil_no}</td>
              <td className="px-5 py-3 text-sm font-semibold text-slate-900">{p.ad_soyad}
                {!Number(p.aylik_brut) && p.aktif !== false && <span data-testid={`maas-bekleniyor-${p.sicil_no}`} className="ml-1.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300">Maaş bekleniyor</span>}
              </td>
              <td className="px-5 py-3 text-sm text-slate-600">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span>{p.pozisyon}</span>
                  {(() => {
                    const rule = getRuleFor(p.pozisyon);
                    if (!rule) return null;
                    const isCC = isCallCenterPozisyon(p.pozisyon) || isCallCenterPozisyon(rule.pozisyon);
                    if (isCC && hideCCRateBadge) return null;
                    const label = isCC
                      ? `CC %${(rule.rate * 100).toFixed(rule.rate * 100 % 1 === 0 ? 0 : 1)}`
                      : `%${(rule.rate * 100).toFixed(rule.rate * 100 % 1 === 0 ? 0 : 1)}`;
                    const tooltip = `Prim Oran Kuralı · ${rule.pozisyon} · ${rule.base === "vekalet" ? "Vekalet" : "Tahsilat"} ücretinin %${(rule.rate * 100).toFixed(2)}'si`;
                    return (
                      <span
                        data-testid={`cc-badge-personnel-${p.sicil_no}`}
                        title={tooltip}
                        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border ${isCC ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-indigo-100 text-indigo-800 border-indigo-300"}`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
              </td>
              <td className="px-5 py-3 text-sm text-right font-mono-tab font-semibold text-slate-900">{canSeeMoney ? formatTL(p.aylik_brut) : "•••••"}</td>
              <td className="px-5 py-3 text-sm text-slate-600 font-mono-tab">{fmtDate(p.ise_baslama_tarihi)}</td>
              {view === "pasif" && (
                <td className="px-5 py-3 text-sm text-rose-700 font-mono-tab font-semibold">{fmtDate(p.ise_cikis_tarihi) || "—"}</td>
              )}
              {view === "pasif" && (
                <td className="px-5 py-3 text-xs text-slate-700">
                  {p.cikis_sebebi
                    ? <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{CIKIS_SEBEP_LABELS[p.cikis_sebebi] || p.cikis_sebebi}</span>
                    : <span className="text-slate-400">—</span>}
                </td>
              )}
              <td className="px-5 py-3">
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${p.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                  {p.aktif ? "Aktif" : "Pasif"}
                </span>
              </td>
              <td className="px-5 py-3 text-right">
                {!p.aktif && canSeeKidem && (
                  <button
                    data-testid={`exit-pdf-${p.sicil_no}`}
                    onClick={() => onExitPdf(p)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-rose-600 hover:bg-rose-50 mr-1"
                    title="Çıkış Tazminatı PDF"
                  ><FileDown size={14} /></button>
                )}
                {docsIndex && <DocsMenu p={p} docs={docsIndex[p.id] || []} onChanged={onDocsChanged} />}
                {docsIndex && canManageStatements && <StatementActions p={p} onChanged={onStatementChanged} views={statementViews} key={`${p.id}-${!!p.ekstre_acik}`} />}
                <button
                  data-testid={`advance-personnel-${p.sicil_no}`}
                  onClick={() => onAdvance(p)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-600 hover:bg-amber-50 mr-1"
                  title="Avans Ekle"
                ><Receipt size={14} /></button>
                {canSeeKidem && (
                  <button
                    data-testid={`kidem-sim-${p.sicil_no}`}
                    onClick={() => onKidemSim(p)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-50 mr-1"
                    title="Kıdem Simülasyonu"
                  ><Award size={14} /></button>
                )}
                <button
                  data-testid={`edit-personnel-${p.sicil_no}`}
                  onClick={() => onEdit(p)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 mr-1"
                ><Edit size={14} /></button>
                <button
                  data-testid={`delete-personnel-${p.sicil_no}`}
                  onClick={() => onDelete(p.id)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-rose-500 hover:bg-rose-50"
                ><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </SortProvider>
    </div>
  );
}
